import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { get, post, put } from "api/api-methods";
import { useBookingId } from "hooks/use-booking-id";
import { useError } from "hooks/use-error";
import queryClient from "query-client";
import { useNavigate } from "react-router-dom";
import { State, Time } from "store/store-types";
import { BookingMenu } from "types/menu";
import {
    CreateReservationParameters,
    removeTime,
    UpdateReservationParameters,
} from "types/request-parameters";
import { WebBooking } from "types/web-booking";
import { isGuid } from "utils";
import { error as logError } from "logger/logger";
import { buildSearchParams } from "components/stepper/use-booking-navigate";

export function useReservation() {
    const id = useBookingId();
    return useQuery({
        queryKey: ["booking", id],
        queryFn: async () => {
            return get(`webBooking/webBookings/${id}`).then((response: Partial<WebBooking>) => {
                return new WebBooking(response);
            });
        },
        enabled: !!id,
    });
}

export const useCreateReservation = () => {
    const { setErrorCode, openReservationFailedModal } = useError();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    // const location = useLocation();

    const createReservation = async (state: State) => {
        const data: CreateReservationParameters = new CreateReservationParameters(state);
        let booking: WebBooking;

        try {
            booking = await post<WebBooking, CreateReservationParameters>(
                `WebBooking/WebBookings`,
                data
            );
            const pathParts = window.location.pathname.split("/").filter((part) => part);

            // Check if the last segment is a GUID
            const lastPart = pathParts[pathParts.length - 1];
            const hasGuidAtEnd = isGuid(lastPart);

            // Replace or append the GUID
            if (hasGuidAtEnd) {
                pathParts[pathParts.length - 1] = booking.guid;
            } else {
                pathParts.push(booking.guid);
            }

            // Reconstruct the path
            const pathname = `/${pathParts.join("/")}`;

            const search = buildSearchParams(
                {
                    guests: null,
                    children: null,
                    date: null,
                },
                location.search
            );
            void navigate(
                {
                    pathname,
                    search,
                },
                { replace: true }
            );

            // update the React Query cache
            queryClient.setQueryData<WebBooking>(
                ["booking", booking.guid],
                new WebBooking(booking)
            );

            return booking;
        } catch (error) {
            const response = (error as { response?: { data?: { code: number } } })?.response?.data;
            const errorCode = response?.code;

            if (typeof errorCode === "number") {
                setErrorCode(errorCode);
                openReservationFailedModal();
            }

            throw error;
        }
    };

    return useMutation({
        mutationFn: createReservation,
        scope: {
            id: "webbooking-create",
        },
    });
};

export const useUpdateReservation = () => {
    const { setErrorCode, openReservationFailedModal } = useError();
    const id = useBookingId();
    const { data: reservation } = useReservation();

    const updateReservation = async ({
        timesToUpdate,
        removeTimes,
    }: {
        state: State;
        timesToUpdate: Time[];
        removeTimes?: removeTime[];
    }) => {
        if (!reservation) {
            throw new Error("Reservation data is required for updating reservation");
        }

        const data: UpdateReservationParameters = new UpdateReservationParameters(
            {
                date: reservation.start.clone().startOf("day"),
                guests: reservation.guests,
                guestsChildren: reservation.guestsChildren,
            },
            timesToUpdate,
            id || "",
            removeTimes
        );

        try {
            const booking = await put<WebBooking, UpdateReservationParameters>(
                `WebBooking/WebBookings`,
                data
            );
            await queryClient.invalidateQueries({
                // Todo: We invalidate it because the updated response does not include the webrule. Should fix this on the backend.
                queryKey: ["booking", id],
            });
            return booking;
        } catch (error) {
            const response = (error as { response?: { data?: { code: number } } })?.response?.data;
            logError(error);
            const errorCode = response?.code;
            if (typeof errorCode === "number") {
                setErrorCode(errorCode);
                openReservationFailedModal();
            }
            throw error;
        }
    };

    return useMutation({
        mutationFn: updateReservation,
        scope: {
            id: "webbooking-create",
        },
    });
};

export const useBookingMenu = () => {
    const { setErrorCode, openReservationFailedModal } = useError();
    const id = useBookingId();

    const updateBookingMenu = async (bookingMenu: BookingMenu) => {
        let menu: BookingMenu[] = [];

        try {
            menu = await post<BookingMenu[], BookingMenu[]>(
                "BookingMenu/BookingMenusExternal/Respond",
                [bookingMenu]
            );
            await queryClient.invalidateQueries({
                queryKey: ["booking", id],
            });
        } catch (error) {
            const response = (error as { response?: { data?: { code: number } } })?.response?.data;
            logError(error);
            const errorCode = response?.code;
            if (typeof errorCode === "number") {
                setErrorCode(errorCode);
                openReservationFailedModal();
            }
        }

        return menu;
    };

    return useMutation({
        mutationFn: updateBookingMenu,
        scope: {
            id: "webbooking-create",
        },
    });
};
